import type { ClosingDocumentSource, IStartClosingRequest } from '../models/closing-setup.model';
import type { IPeriodBounds } from '../models/project-account.model';

/**
 * The company's ERP connection, as the start sends it — the same shape «ERP aktualisieren» sends to
 * `POST …/sync`. A per-org vendor (Abacus, Topal, D365 BC) carries its connection and the client's id
 * on the vendor side; Bexio carries neither, because its connection hangs off the company.
 */
export interface StartClosingConnection {
    readonly vendor: string;
    readonly connectionId: string | null;
    readonly externalCompanyId: string | null;
}

export interface StartClosingAnswers {
    readonly connection: StartClosingConnection;
    readonly documentSource: ClosingDocumentSource | null;
    /** The previous year, pulled as a comparison when the ERP has it. Empty when unknown. */
    readonly comparisonPeriods: readonly IPeriodBounds[];
}

/**
 * The wizard's answers, as the start payload.
 *
 * Always an API closing: the accounting step only lets a closing start with a connected ERP, and the
 * file route is not offered yet.
 */
export function startClosingRequestOf(answers: StartClosingAnswers): IStartClosingRequest {
    const { connection } = answers;

    return {
        accountingSource: 'API',
        erpVendor: connection.vendor,
        ...(connection.connectionId === null ? {} : { connectionId: connection.connectionId }),
        ...(connection.externalCompanyId === null ? {} : { externalCompanyId: connection.externalCompanyId }),
        ...(answers.comparisonPeriods.length === 0
            ? {}
            : {
                  comparisonPeriods: answers.comparisonPeriods.map((period) => ({
                      periodStart: period.periodStart,
                      periodEnd: period.periodEnd,
                  })),
              }),
        documentSource: answers.documentSource ?? 'MANUAL',
    };
}

/**
 * The business year before `period`: it ends the day before `period` starts and begins one year
 * earlier. The same rule as the workspace's sync dialog (`suggestedPullPeriods`), so a non-calendar
 * fiscal year steps back correctly.
 */
export function previousPeriodOf(period: IPeriodBounds): IPeriodBounds {
    return { periodStart: oneYearBefore(period.periodStart), periodEnd: dayBefore(period.periodStart) };
}

/**
 * The closing's period from its balance-sheet date alone — the twelve months ending on it.
 *
 * Only a fallback for when the closing's own period is not known yet; `null` for a date that does
 * not parse.
 */
export function periodEndingOn(balanceSheetDate: string | null): IPeriodBounds | null {
    if (
        balanceSheetDate === null ||
        Number.isNaN(Date.parse(`${balanceSheetDate.slice(0, ISO_DATE_LENGTH)}T00:00:00.000Z`))
    ) {
        return null;
    }
    const periodEnd = balanceSheetDate.slice(0, ISO_DATE_LENGTH);

    return { periodStart: dayAfter(oneYearBefore(periodEnd)), periodEnd };
}

/** Whether a journal for exactly this period is stored. */
export function holdsPeriod(held: readonly IPeriodBounds[], period: IPeriodBounds): boolean {
    return held.some(
        (candidate) => candidate.periodStart === period.periodStart && candidate.periodEnd === period.periodEnd,
    );
}

const ISO_DATE_LENGTH = 'YYYY-MM-DD'.length;
const MILLISECONDS_PER_DAY = 86_400_000;

function dayBefore(iso: string): string {
    return isoOf(new Date(Date.parse(`${iso}T00:00:00.000Z`) - MILLISECONDS_PER_DAY));
}

function dayAfter(iso: string): string {
    return isoOf(new Date(Date.parse(`${iso}T00:00:00.000Z`) + MILLISECONDS_PER_DAY));
}

/** The same month and day, one year earlier — in UTC, so no timezone moves it across a boundary. */
function oneYearBefore(iso: string): string {
    const date = new Date(`${iso}T00:00:00.000Z`);
    date.setUTCFullYear(date.getUTCFullYear() - 1);

    return isoOf(date);
}

function isoOf(date: Date): string {
    return date.toISOString().slice(0, ISO_DATE_LENGTH);
}
